import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import PauseInputRow from '@/components/ui/PauseInputRow';
import { Plus, AlertTriangle, XCircle } from 'lucide-react';

const PauseEditor = ({ 
  pauses = [], 
  onPausesChange, 
  bruttoMinutes = 0, 
  errors = {} 
}) => {
  // Initialize pause list from props
  const initialPauseList = useMemo(() => {
    if (pauses && pauses.length > 0) {
      // Convert from backend format (objects with duration_minutes) to frontend format (numbers)
      return pauses.map(pause => 
        typeof pause === 'object' ? pause.duration_minutes : pause
      );
    } else {
      return [null]; // Start with one empty pause
    }
  }, [pauses]);

  const [pauseList, setPauseList] = useState(initialPauseList);
  
  const addPause = () => {
    if (pauseList.length >= 15) return; // Max limit
    
    const newPauses = [...pauseList, null];
    setPauseList(newPauses);
    onPausesChange(newPauses.filter(p => p !== null));
  };
  
  const removePause = (index) => {
    const newPauses = pauseList.filter((_, i) => i !== index);
    // Ensure at least one pause row remains
    const finalPauses = newPauses.length === 0 ? [null] : newPauses;
    setPauseList(finalPauses);
    onPausesChange(finalPauses.filter(p => p !== null));
  };
  
  const updatePause = (index, value) => {
    const newPauses = [...pauseList];
    newPauses[index] = value;
    setPauseList(newPauses);
    onPausesChange(newPauses.filter(p => p !== null));
  };
  
  const removeAllPauses = () => {
    setPauseList([null]);
    onPausesChange([]);
  };
  
  // Berechne Gesamtpause und Netto-Zeit
  const validPauses = pauseList.filter(pause => pause !== null && pause > 0);
  const totalPauseMinutes = validPauses.reduce((sum, pause) => sum + pause, 0);
  const nettoMinutes = Math.max(0, bruttoMinutes - totalPauseMinutes);
  const pauseSumExceedsBrutto = totalPauseMinutes > bruttoMinutes;
  const pauseSumIsHigh = totalPauseMinutes > bruttoMinutes * 0.5;
  
  const formatMinutesToHM = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-blue-50">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Pausen hinzufügen</h4>
        <div className="flex gap-2">
          {pauseList.length > 3 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={removeAllPauses}
              className="text-red-600"
            >
              Alle löschen
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPause}
            disabled={pauseList.length >= 15}
            className="text-blue-600"
          >
            <Plus className="h-4 w-4 mr-1" />
            Pause hinzufügen
          </Button>
        </div>
      </div>
      
      {/* Limit-Warnung */}
      {pauseList.length >= 15 && (
        <div className="p-2 bg-amber-100 border border-amber-300 rounded text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          Maximal 15 Pausen möglich. Fassen Sie ähnliche Pausen zusammen.
        </div>
      )}
      
      {/* Pausen-Liste - Scrollable with better max height */}
      <div className={`space-y-3 ${
        pauseList.length > 5 
          ? 'max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-blue-100' 
          : ''
      }`}>
        {pauseList.map((pause, index) => (
          <PauseInputRow
            key={index}
            value={pause}
            onChange={(value) => updatePause(index, value)}
            onRemove={() => removePause(index)}
            showRemove={pauseList.length > 1}
            error={errors[`pause_${index}`]}
          />
        ))}
        
        {/* Scroll indicator */}
        {pauseList.length > 5 && (
          <div className="text-xs text-center text-blue-600 py-2 border-t border-blue-200">
            ↕️ Scrolle für weitere Pausen ({pauseList.length} von max. 15)
          </div>
        )}
      </div>
      
      {/* Zusammenfassung - Always visible at bottom */}
      <div className="pt-3 border-t border-blue-200 bg-blue-50 sticky bottom-0">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Bruttozeit:</span>
            <div className="font-medium">{formatMinutesToHM(bruttoMinutes)}</div>
          </div>
          <div>
            <span className="text-gray-600">Pausen:</span>
            <div className={`font-medium ${
              pauseSumExceedsBrutto ? 'text-red-600' : 
              pauseSumIsHigh ? 'text-yellow-600' : 
              'text-gray-900'
            }`}>
              {formatMinutesToHM(totalPauseMinutes)}
            </div>
          </div>
          <div>
            <span className="text-gray-600">Nettozeit:</span>
            <div className={`font-medium ${
              pauseSumExceedsBrutto ? 'text-red-600' : 'text-green-600'
            }`}>
              {formatMinutesToHM(nettoMinutes)}
            </div>
          </div>
        </div>
        
        {/* Fehler-/Warnmeldungen */}
        {pauseSumExceedsBrutto && (
          <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-700">
            <XCircle className="h-4 w-4 inline mr-1" />
            Pausen-Summe ({formatMinutesToHM(totalPauseMinutes)}) kann nicht größer als Arbeitszeit ({formatMinutesToHM(bruttoMinutes)}) sein
          </div>
        )}
        
        {pauseSumIsHigh && !pauseSumExceedsBrutto && (
          <div className="mt-2 p-2 bg-amber-100 border border-amber-300 rounded text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 inline mr-1" />
            Warnung: Pausen machen mehr als 50% der Arbeitszeit aus
          </div>
        )}
      </div>
    </div>
  );
};

export default PauseEditor;